import { useCallback, useEffect, useState } from "react";
import { BrowserRouter, Navigate, NavLink, Route, Routes } from "react-router-dom";
import { api, type User } from "./lib/api";
import { LoginPage } from "./pages/LoginPage";
import { DashboardPage } from "./pages/DashboardPage";
import { CamerasPage } from "./pages/CamerasPage";
import { LivePage } from "./pages/LivePage";
import { PlaybackPage } from "./pages/PlaybackPage";
import { UsersPage } from "./pages/UsersPage";
import { PermissionsPage } from "./pages/PermissionsPage";
import { StoragePage } from "./pages/StoragePage";
import { LogsPage } from "./pages/LogsPage";
import { SystemPage } from "./pages/SystemPage";
import { PreferencesPage } from "./pages/PreferencesPage";

interface NavigationItem { to: string; icon: string; label: string; permission?: string }
const navigation: NavigationItem[] = [
  { to: "/dashboard", icon: "bi-speedometer2", label: "현황" },
  { to: "/live", icon: "bi-grid-3x3-gap", label: "실시간 보기", permission: "live.view" },
  { to: "/playback", icon: "bi-play-circle", label: "녹화 영상", permission: "recording.search" },
  { to: "/cameras", icon: "bi-camera-video", label: "카메라", permission: "camera.view" },
  { to: "/storage", icon: "bi-device-hdd", label: "저장소", permission: "storage.configure" },
  { to: "/users", icon: "bi-people", label: "사용자" },
  { to: "/permissions", icon: "bi-shield-lock", label: "권한", permission: "permission.manage" },
  { to: "/system", icon: "bi-gear", label: "시스템", permission: "system.configure" },
  { to: "/logs", icon: "bi-journal-text", label: "로그", permission: "log.view" },
  { to: "/settings", icon: "bi-sliders", label: "내 설정" }
];

export default function App() {
  const [user,setUser] = useState<User|null|undefined>(undefined);
  const [sidebarCollapsed,setSidebarCollapsed] = useState(() => localStorage.getItem("nvr_sidebar_collapsed") === "true");
  const refreshUser = useCallback(async () => {
    const result = await api<{user:User}>("/api/auth/me");
    setUser(result.user);
  }, []);
  useEffect(() => {
    void refreshUser().catch(() => setUser(null));
    const unauthorized = () => setUser(null);
    const refreshVisibleUser = () => {
      if (document.visibilityState === "visible") void refreshUser().catch(() => undefined);
    };
    window.addEventListener("nvr:unauthorized", unauthorized);
    window.addEventListener("focus", refreshVisibleUser);
    document.addEventListener("visibilitychange", refreshVisibleUser);
    return () => {
      window.removeEventListener("nvr:unauthorized", unauthorized);
      window.removeEventListener("focus", refreshVisibleUser);
      document.removeEventListener("visibilitychange", refreshVisibleUser);
    };
  }, [refreshUser]);
  useEffect(() => {
    if (!user) return;
    setSidebarCollapsed(user.sidebarCollapsed);
    localStorage.setItem("nvr_sidebar_collapsed", String(user.sidebarCollapsed));
  }, [user?.id, user?.sidebarCollapsed]);
  if (user === undefined) return <div className="loading">NVR 시작 중…</div>;
  if (!user) return <LoginPage onLogin={setUser}/>;

  const toggleSidebar = () => setSidebarCollapsed((current) => {
    const next = !current;
    localStorage.setItem("nvr_sidebar_collapsed", String(next));
    return next;
  });

  const logout = () => void api("/api/auth/logout", { method: "POST" }).finally(() => setUser(null));

  return <BrowserRouter><div className={`app-shell ${sidebarCollapsed ? "sidebar-collapsed" : ""}`}>
    <aside className="sidebar">
      <div className="sidebar-brand"><i className="bi bi-camera-video-fill"/><span>NVR</span><button type="button" className="sidebar-toggle" onClick={toggleSidebar} aria-label={sidebarCollapsed ? "메뉴 펼치기" : "메뉴 접기"} title={sidebarCollapsed ? "메뉴 펼치기" : "메뉴 접기"}><i className={`bi bi-chevron-${sidebarCollapsed ? "right" : "left"}`} /></button></div>
      <nav>{navigation.filter((item) => !item.permission || user.permissions.includes(item.permission)).map((item) =>
        <NavLink to={item.to} key={item.to} title={sidebarCollapsed ? item.label : undefined}><i className={`bi ${item.icon}`}/><span>{item.label}</span></NavLink>)}</nav>
    </aside>
    <main className="content">
      <header><div className="account-menu"><NavLink className="user-button" to="/users" title="내 계정 관리"><strong>{user.displayName}</strong><span aria-hidden="true"> · </span><span>{user.username}</span></NavLink><button type="button" className="header-logout" onClick={logout}>로그아웃</button></div></header>
      <section className="page"><Routes>
        <Route path="/" element={<Navigate to={defaultPagePath(user)} replace/>}/>
        <Route path="/dashboard" element={<DashboardPage/>}/>
        <Route path="/live" element={<LivePage user={user} sidebarCollapsed={sidebarCollapsed} onToggleSidebar={toggleSidebar}/>}/>
        <Route path="/playback" element={<PlaybackPage user={user}/>}/>
        <Route path="/cameras" element={user.permissions.includes("camera.view") ? <CamerasPage user={user}/> : <Navigate to={defaultPagePath(user)} replace/>}/>
        <Route path="/storage" element={<StoragePage user={user}/>}/>
        <Route path="/users" element={<UsersPage currentUser={user}/>}/>
        <Route path="/permissions" element={<PermissionsPage onPermissionsChanged={refreshUser}/>}/>
        <Route path="/system" element={<SystemPage/>}/>
        <Route path="/logs" element={<LogsPage/>}/>
        <Route path="/settings" element={<PreferencesPage user={user} onUserChange={setUser}/>}/>
        <Route path="*" element={<NotFound/>}/>
      </Routes></section>
    </main>
  </div></BrowserRouter>;
}

function defaultPagePath(user: User): string {
  const permissionByPage: Record<string, string> = { live: "live.view", playback: "recording.search", cameras: "camera.view", storage: "storage.configure", permissions: "permission.manage", system: "system.configure", logs: "log.view" };
  const required = permissionByPage[user.defaultPage];
  if (required && !user.permissions.includes(required)) return "/dashboard";
  const paths: Record<string, string> = { dashboard: "/dashboard", live: "/live", playback: "/playback", cameras: "/cameras", storage: "/storage", users: "/users", permissions: "/permissions", system: "/system", logs: "/logs", settings: "/settings" };
  return paths[user.defaultPage] ?? "/dashboard";
}

function NotFound() {
  return <div className="panel empty-state">요청한 화면을 찾을 수 없습니다.</div>;
}
